import { gql } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useNotificationContext } from 'core/context/Notifications';
import { addInvitePrefix, pathPrefixByMode } from 'support/location';
import {
  Mode,
  useCompletionGetTrackQuery,
  useCompletionScreenSaveRatingMutation,
  useCompletionScreenUpdateTrackReviewMutation,
  useCompletionStopTrackMutation,
} from 'types/graphql';

import CompletionScreen from '../../organisms/completion-screen/CompletionScreen';

export interface CompletionScreenContainerProps {
  mode: Mode;
  teamSlug: string;
  trackSlug: string;
  inviteID?: string;
}

const CompletionScreenContainer = ({ mode, teamSlug, trackSlug, inviteID }: CompletionScreenContainerProps) => {
  const { dispatch } = useNotificationContext();
  const history = useHistory();
  const { data } = useCompletionGetTrackQuery({
    variables: { trackSlug, teamSlug },
  });
  const [updateReview, { loading: loadingUpdateReview, error }] = useCompletionScreenUpdateTrackReviewMutation();
  const [stopTrack, { loading: loadingStopTrack }] = useCompletionStopTrackMutation();
  const [saveRating, { loading: loadingSaveRating }] = useCompletionScreenSaveRatingMutation();
  const [feedbackState, setFeedbackState] = useState<'vote' | 'write' | 'thanks'>('vote');
  const [reviewID, setReviewID] = useState<string>();

  const onFinish = useCallback(async () => {
    try {
      await stopTrack({ variables: { trackID: data?.track?.id! } });
    } finally {
      if (mode === 'embed') {
        history.push(`${pathPrefixByMode(mode)}/${teamSlug}/tracks/${trackSlug}`);
      } else {
        history.push(addInvitePrefix(data?.track?.permalink || '', inviteID), history.location.state);
      }
    }
  }, [data, history, mode, inviteID, teamSlug, stopTrack, trackSlug]);

  const onSubmitScore = useCallback(
    async (score: number) => {
      if (score < 4) {
        try {
          const result = await saveRating({
            variables: {
              trackID: data?.track?.id!,
              participantID: data?.track?.participant?.id!,
              score,
            },
          });
          if (result?.data?.createTrackReview?.id) {
            setReviewID(result.data.createTrackReview.id);
            setFeedbackState('write');
          }
        } catch (err) {
          dispatch('error', 'Failed to save rating', 'Something went wrong while saving rating, please try again.', 5);
        }
      } else {
        setFeedbackState('thanks');
      }
    },
    [data, saveRating, dispatch],
  );

  const onUpdateReview = useCallback(
    async (content: string) => {
      await updateReview({
        variables: {
          id: reviewID!,
          content,
        },
      });
      setFeedbackState('thanks');
    },
    [reviewID, updateReview],
  );

  return (
    <CompletionScreen
      feedbackState={feedbackState}
      loading={loadingUpdateReview || loadingStopTrack || loadingSaveRating}
      error={error}
      onUpdateReview={onUpdateReview}
      onSubmitScore={onSubmitScore}
      onFinish={onFinish}
    />
  );
};

gql`
  query CompletionGetTrack($trackSlug: String!, $teamSlug: String!) {
    track(trackSlug: $trackSlug, teamSlug: $teamSlug) {
      id
      title
      permalink
      challenges {
        permalink
      }
      participant {
        id
      }
    }
  }
`;

gql`
  mutation CompletionScreenUpdateTrackReview($id: String!, $content: String!) {
    updateTrackReview(id: $id, content: $content) {
      id
    }
  }
`;

gql`
  mutation CompletionScreenSaveRating($trackID: String!, $participantID: String!, $score: Int!) {
    createTrackReview(trackID: $trackID, participantID: $participantID, score: $score) {
      id
    }
  }
`;

gql`
  mutation CompletionStopTrack($trackID: String!) {
    stopTrack(trackID: $trackID) {
      id
      status
      challenges {
        id
        slug
        title
        teaser
        status
      }
    }
  }
`;

export default CompletionScreenContainer;
